import React, { useEffect, useState, useCallback } from 'react';
import { Box, CircularProgress, Alert } from '@mui/material';
import ApplicationOverviewPanel from './ApplicationOverviewPanel';
import ApplicationSettings from './ApplicationSettings';
import QuestionsPanel from './QuestionsPanel';
import ApplicationHelpPanel from './ApplicationHelpPanel';
import { Campaign, CampaignStatus, CampaignMode } from '../../../../types/Campaign';
import { ApplicationDTO, ApplicationUpdateDTO, ApplicationMetricsDTO } from '../../../../types/Applications';
import ApplicationService from '../../../../services/ApplicationService';

interface ApplicationPanelProps {
    campaign: Campaign; // contains ids for fetch
    onAvailabilityChange?: (checked: boolean) => void;
    onLocationChange?: (checked: boolean) => void;
    onCampaignModeChange?: (mode: CampaignMode) => void;
}

/**
 * Aggregates Application Overview, Settings, and Help panels into a single scrollable view.
 * Display order: Overview → Settings → Help.
 */
const ApplicationPanel: React.FC<ApplicationPanelProps> = ({
    campaign,
    onAvailabilityChange = () => { },
    onLocationChange = () => { },
    onCampaignModeChange = () => { },
}) => {
    const [application, setApplication] = useState<ApplicationDTO | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [metrics, setMetrics] = useState<ApplicationMetricsDTO | null>(null);

    // Add read-only flag based on campaign status
    const isReadOnly = campaign && 'status' in campaign ? campaign.status !== CampaignStatus.PREP : false;

    // Fetch application and metrics when component mounts or campaign changes
    useEffect(() => {
        const fetchAll = async () => {
            try {
                const svc = new ApplicationService();
                const data = await svc.getApplication(campaign.organization_id, campaign.id);
                setApplication(data);
                const m = await svc.getApplicationMetrics(campaign.organization_id, campaign.id);
                setMetrics(m);
            } catch (err) {
                console.error('Failed to fetch application or metrics', err);
            } finally {
                setLoading(false);
            }
        };
        fetchAll();
    }, [campaign.organization_id, campaign.id]);

    const persistUpdate = useCallback(async (update: Partial<ApplicationUpdateDTO>) => {
        try {
            const svc = new ApplicationService();
            await svc.updateApplication(campaign.organization_id, campaign.id, update);
            setApplication((prev) => prev ? { ...prev, ...update } as ApplicationDTO : prev);
        } catch (err) {
            console.error('Failed to update application', err);
        }
    }, [campaign.id, campaign.organization_id]);

    const handleAvailChange = useCallback((includeAvailability: boolean) => {
        if (!application) return;
        const updated = { ...application, include_availability: includeAvailability };
        setApplication(updated);
        onAvailabilityChange(includeAvailability);
        persistUpdate({ include_availability: includeAvailability });
    }, [application, onAvailabilityChange, persistUpdate]);

    const handleLocationChange = useCallback((includeLocation: boolean) => {
        if (!application) return;
        const updated = { ...application, include_location: includeLocation };
        setApplication(updated);
        onLocationChange(includeLocation);
        persistUpdate({ include_location: includeLocation });
    }, [application, onLocationChange, persistUpdate]);

    const handleModeChange = useCallback((mode: CampaignMode) => {
        if (!application) return;
        const updated = { ...application, campaign_mode: mode };
        setApplication(updated);
        onCampaignModeChange(mode);
        persistUpdate({ campaign_mode: mode });
    }, [application, onCampaignModeChange, persistUpdate]);

    if (loading) {
        return (
            <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!application) {
        return (
            <Alert severity="warning" sx={{ m: 2 }}>
                No application data available.
            </Alert>
        );
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Contextual help for application creation (moved to top) */}
            <ApplicationHelpPanel initialShowHelp={false} />

            {/* Application metrics / stats overview */}
            <ApplicationOverviewPanel
                organizationId={campaign.organization_id}
                campaignId={campaign.id}
                metrics={metrics}
            />

            {/* Application settings editor */}
            <ApplicationSettings
                applicationDetails={application}
                onAvailabilityChange={handleAvailChange}
                onLocationChange={handleLocationChange}
                onCampaignModeChange={handleModeChange}
                isReadOnly={isReadOnly}
            />

            {/* Questions list */}
            <QuestionsPanel
                campaignId={campaign.id}
                organizationId={campaign.organization_id}
                onMetricsChange={setMetrics}
                isReadOnly={isReadOnly}
            />
        </Box>
    );
};

export default ApplicationPanel;
